import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, BadgeCheck, Award, CalendarClock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SmartImage from '../components/SmartImage';
import api, { mediaUrl } from '../lib/api';

const GENDERS = [['', 'All'], ['male', 'Male'], ['female', 'Female'], ['other', 'Other']];
const AVAIL = [['', 'Any'], ['full_time', 'Full Time'], ['part_time', 'Part Time']];

// Normalizers: the public directory shows BOTH `teachers` (/api/teachers) and
// `facilitator_profiles` (/api/facilitators) — two separate backend systems.
const normTeacher = (t) => ({
  key: `t-${t.id}`, testid: t.slug, to: `/teachers/${t.slug}`,
  name: t.name, role: t.role, photo_url: t.photo_url,
  bio: t.short_bio, years: t.years_experience, verified: t.is_verified,
  gender: t.gender, avail: t.availability,
  offers: t.offers_private_lessons && t.private_lesson_status === 'approved',
  private_free: (t.session_types || []).length > 0 && !(t.session_types || []).some((s) => Number(s.price || 0) > 0),
});
const normFacilitator = (f) => ({
  key: `f-${f.id}`, testid: f.id, to: `/facilitators/${f.id}`,
  name: f.display_name, role: f.title, photo_url: f.photo_url,
  bio: f.bio, years: f.years_experience, verified: f.is_verified,
  gender: f.gender, avail: f.availability,
});

export const PersonCard = ({ p }) => {
  const navigate = useNavigate();
  return (
  <Link to={p.to} data-testid={`teacher-card-${p.testid}`}
    className="group bg-white/70 rounded-3xl overflow-hidden border border-rose/40 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 block">
    <SmartImage src={mediaUrl(p.photo_url)} alt={p.name} className="aspect-[3/4]">
      {p.verified && <div className="absolute top-3 right-3 z-10 rounded-full bg-green-600 text-white text-[11px] px-2.5 py-1 flex items-center gap-1"><BadgeCheck className="w-3.5 h-3.5" /> Verified</div>}
      {p.offers && <div className="absolute bottom-3 left-3 z-10 rounded-full bg-mahogany/90 text-cream text-[11px] px-2.5 py-1 flex items-center gap-1" data-testid={`private-badge-${p.testid}`}><CalendarClock className="w-3.5 h-3.5" /> {p.private_free ? 'Free sessions' : 'Private sessions'}</div>}
    </SmartImage>
    <div className="p-6">
      <h3 className="font-serif-display text-burgundy text-2xl leading-tight mb-1 group-hover:text-rose-italic transition-colors">{p.name}</h3>
      {p.role && <div className="text-xs uppercase tracking-wider text-mahogany/60 mb-2">{p.role}</div>}
      {p.bio && <p className="text-sm text-burgundy/80 line-clamp-3">{p.bio}</p>}
      {p.years ? <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-mahogany/70"><Award className="w-3.5 h-3.5 text-rose-italic" /> {p.years}+ years</div> : null}
      {p.offers && (
        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`${p.to}?book=1`); }}
          className="pill-btn mt-4 text-sm w-full justify-center" data-testid={`book-${p.testid}`}>
          <CalendarClock className="w-4 h-4" /> {p.private_free ? 'Request a free session' : 'Book a session'}
        </button>
      )}
    </div>
  </Link>
  );
};

const TeachersDirectory = () => {
  const [params] = useSearchParams();
  const privateOnly = params.get('private') === '1';
  const [gender, setGender] = useState('');
  const [availability, setAvailability] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get('/teachers').then((r) => r.data || []).catch(() => [])
      .then((teachers) => {
        let merged = teachers.map(normTeacher);
        if (privateOnly) merged = merged.filter((p) => p.offers);
        if (gender) merged = merged.filter((p) => p.gender === gender);
        if (availability) merged = merged.filter((p) => p.avail === availability);
        setItems(merged);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [gender, availability, privateOnly]);

  return (
    <div className="App" data-testid="page-teachers">
      <Header />
      <main className="bg-cream min-h-[60vh]">
        <section className="max-w-[1300px] mx-auto px-6 lg:px-10 py-16">
          <div className="text-center">
            <div className="eyebrow mb-3">Areas of Specialty</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-burgundy leading-tight">Facilitators &amp; <span className="font-italic-display text-rose-italic">Teachers</span></h1>
            <p className="mt-5 text-mahogany/80 max-w-2xl mx-auto">Meet the guides who hold space for your journey — seasoned facilitators and disciples of Osho, here to walk beside you toward awareness and celebration.</p>
            <Link to="/dashboard/facilitator" data-testid="apply-teacher-cta" className="pill-btn mt-6 inline-flex">
              <Award className="w-4 h-4" /> Apply as a Teacher / Facilitator
            </Link>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <Select value={gender} onChange={setGender} options={GENDERS} testid="teacher-gender-filter" />
            <Select value={availability} onChange={setAvailability} options={AVAIL} testid="teacher-availability-filter" />
          </div>

          {loading ? (
            <div className="flex justify-center mt-16"><Loader2 className="w-8 h-8 animate-spin text-mahogany" /></div>
          ) : items.length === 0 ? (
            <div className="mt-16 text-center text-mahogany/60" data-testid="teachers-empty">No teachers match your filters.</div>
          ) : (
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="teachers-grid">
              {items.map((p) => <PersonCard key={p.key} p={p} />)}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

const Select = ({ value, onChange, options, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className="bg-white/70 rounded-full border border-rose/50 px-4 py-2 text-burgundy focus:outline-none">
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

export default TeachersDirectory;
